import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'sonner'

import { Avatar } from "@material-tailwind/react";
import DataTableShell from '../../../Components/table/DataTableShell';
import TableToolbar from '../../../Components/table/TableToolbar';
import TableSearch from '../../../Components/table/TableSearch';
import {
    TableActionGroup,
    TableEmptyRow,
    TableSkeletonRows,
    TableStatusChip,
} from '../../../Components/table/TableCells';
import useClientTable from '../../../Components/table/useClientTable';

import { HiPencilAlt } from "react-icons/hi";
import { FaUserAltSlash } from "react-icons/fa";
import { FaUserCheck } from "react-icons/fa";
import { HiTrash } from "react-icons/hi2"

const TABLE_HEAD = ["Avatar", "Name", "Email", "Status", "Role", "Permissions", "Actions"];
const filterUser = (user, query) =>
    !query ||
    user.firstName?.toLowerCase().includes(query) ||
    user.lastName?.toLowerCase().includes(query) ||
    user.email?.toLowerCase().includes(query);
const sortUsers = (a, b) => (a.firstName || '').localeCompare(b.firstName || '');

export default function AdminTable({ updateIsEditable, updateTable }) {

    const [users, setUsers] = useState([]);
    const [isAltered, setIsAltered] = useState(false);
    const [userPermissions, setUsersPermissions] = useState([])
    const [editCredentials, setEditCredentials] = useState({ role: '', firstName: '', lastName: '' })
    const [isLoad, setIsLoad] = useState(false)
    const [isEditable, setIsEditable] = useState(false);
    const usersPerPage = 9;
    const {
        searchTerm,
        handleSearch,
        currentPage,
        setCurrentPage,
        pageItems: currentUsers,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: users,
        pageSize: usersPerPage,
        filterFn: filterUser,
        sortFn: sortUsers,
    });

    useEffect(() => {
        axios.get('/api/departments/get')
            .then(response => {
                if (response.data.message) {
                    console.log('Cannot get departments')
                } else {              
                    setUsersPermissions(response.data.result)
                }
            })
    }, []);

    useEffect(() => {
        setIsLoad(true)
        axios.get('/api/users/get-users?role=&isActive=')
            .then(response => {
                if (response.data.message) {
                    console.log("Cannot get users");
                } else {
                    setUsers(response.data.result); 
                }
                setIsLoad(false)
            })
            .catch(error => {
                console.error('Error:', error);
            });
    }, [isAltered, updateTable]);

    const toggleEdit = (userId) => {
        setIsEditable(prevState => (prevState === userId ? false : userId));
        updateIsEditable(prevState => !prevState);
    };
    // USER HANDLERS
    const handleEditUser = (id, role, firstName, lastName) => {

        const updatedCredentials = {
            role: editCredentials.role || role || '',
            firstName: editCredentials.firstName || firstName || '',
            lastName: editCredentials.lastName || lastName || ''
        };
    
        console.log("editCredentials: ", updatedCredentials);
    
        axios.put(`/api/users/update-user/${id}`, updatedCredentials)
            .then(response => {
                if (response.data.message) {
                    toast.success(`${response.data.message}`);
                    setEditCredentials({ role: '',  firstName:  '', lastName: '' });
                    toggleEdit(false);
                    setIsAltered(prevState => !prevState);
                } else {    
                    toast.error(`${response.data.message}`);
                }
            })
            .catch(error => {
                console.error('Error:', error);
                const errorMessage = error.response?.data?.message || 'An error occurred during add.';
                toast.error(errorMessage);
            });
    };

    const handleUserPermissions = (id, department, departmentId) => {
        console.log('department: ', department)
        console.log('departmentId: ', departmentId)
        const addPermissions = [
            {
                departmentId: departmentId,
                department: department,
                accessibility: true
            }
        ];
                                                    
        axios.get(`/api/users/get-user/${id}`)
            .then((response) => {
                const currUserPermissions = response.data.permissions.map(permission => permission.department);
                // Check if department already exists
                if (currUserPermissions.includes(department)) {
                    toast.info(`Department "${department}" already exists for user ${id}. No action taken.`);
                    return;
                } else {

                    console.log("Added department: ", addPermissions);
    
                    axios.put(`/api/users/${id}/permissions`, addPermissions)
                        .then(response => {
                            if (response.data.message) {
                                console.log("Updated department: ", addPermissions);
                                setIsAltered(prevState => !prevState);
                            } else {
                                toast.error(`Update failed: ${response.data.message}`);
                            }
                        })
                        .catch(error => {
                            console.error('Error:', error);
                            const errorMessage = error.response?.data?.message || 'An error occurred during the update.';
                            toast.error(errorMessage);
                        })
                }
            })
            .catch((error) => {
                console.error('Error fetching user data:', error);
            });
    };
    
    const handleToggleUserStatus = (isActive, id, firstName) => {
        const updatedUserData = {
            isActive: !isActive 
        };
    
        axios.put(`/api/users/update-user/${id}`, updatedUserData)
            .then(response => {
                if (response.data.message) {
                    toast.info(`User ${firstName}: ${isActive === true ? 'Disabled' : 'Activated'}`);
                    console.log("updated: ", updatedUserData)
                    setIsAltered(prevState => !prevState);
                } else {
                    toast.error(`${response.data.message}`);
                }
            })
            .catch(error => {
                console.error('Error:', error);
                const errorMessage = error.response?.data?.message || 'An error occurred during add.';
                toast.error(errorMessage);
            });
    };
    
    const handleDeleteUser = (id, firstName, lastName) => {
        const isConfirmed = window.confirm(`Are you sure you want to delete the user ${firstName} ${lastName}?`);

        if (isConfirmed) {
            axios.delete(`/api/users/delete-user/${id}`)
                .then(response => {
                    if (response.data.message) {
                        setIsAltered(prevState => !prevState);
                        toast.success(`${response.data.message}`);
                        // setUsers(prevUsers => prevUsers.filter((idx) => idx !== index));
                        
                    } else {
                        toast.error(`${response.data.message}`);
                        console.log('Error:', response.data.error);
                    }
                })
                .catch(error => {
                    console.error('Error:', error);
                });
        }
    }

    return (
        <DataTableShell
            minWidth="980px"
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            toolbar={
                <TableToolbar
                    kicker="Administration"
                    title="User management"
                    subtitle={`${totalFiltered} matching users · ${users.length} total`}
                >
                    <TableSearch
                        id="user-management-search"
                        value={searchTerm}
                        onChange={handleSearch}
                        placeholder="Search users"
                    />
                </TableToolbar>
            }
        >
            <thead>
                <tr>
                    {TABLE_HEAD.map((head) => <th key={head}>{head}</th>)}
                </tr>
            </thead>
            <tbody>
                {isLoad ? (
                    <TableSkeletonRows columns={TABLE_HEAD.length} rows={usersPerPage} />
                ) : currentUsers.length === 0 ? (
                    <TableEmptyRow
                        columns={TABLE_HEAD.length}
                        title="No users found"
                        description="Try a different name or email."
                    />
                ) : currentUsers.map((user) => {
                    const {
                        _id,
                        firstName,
                        lastName,
                        email,
                        role,
                        isActive,
                        profileImage,
                        permissions = [],
                    } = user;
                    const avatarUrl = `https://nexus-portal.s3.amazonaws.com/profileImages/${profileImage}`;
                    const isEditing = isEditable === _id;

                    return (
                        <tr key={_id} className={!isActive ? 'nx-table-row-selected' : ''}>
                            <td>
                                <Avatar
                                    src={profileImage ? avatarUrl : 'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_1280.png'}
                                    alt={`${firstName} ${lastName}`}
                                    size="sm"
                                    className={`border-2 ${isActive ? 'border-blue-500' : 'border-red-500'}`}
                                />
                            </td>
                            <td>
                                {isEditing ? (
                                    <div className="flex min-w-[14rem] gap-2">
                                        <input
                                            className="nx-input"
                                            defaultValue={firstName}
                                            onChange={(event) => setEditCredentials({ ...editCredentials, firstName: event.target.value })}
                                        />
                                        <input
                                            className="nx-input"
                                            defaultValue={lastName}
                                            onChange={(event) => setEditCredentials({ ...editCredentials, lastName: event.target.value })}
                                        />
                                    </div>
                                ) : (
                                    <span className="font-medium text-paper">{firstName} {lastName}</span>
                                )}
                            </td>
                            <td className="text-muted">{email}</td>
                            <td>
                                <TableStatusChip
                                    status={isActive ? 'Active' : 'Disabled'}
                                    tone={isActive ? 'ok' : 'bad'}
                                />
                            </td>
                            <td>
                                {isEditing ? (
                                    <select
                                        id={`role-${_id}`}
                                        className="nx-input min-w-[7rem]"
                                        required
                                        defaultValue={role}
                                        onChange={(event) => setEditCredentials({ ...editCredentials, role: event.target.value })}
                                    >
                                        <option disabled>Select Role</option>
                                        <option value="User">User</option>
                                        <option value="Admin">Admin</option>
                                    </select>
                                ) : (
                                    <span className="nx-chip-idle">{role}</span>
                                )}
                            </td>
                            <td>
                                <div className="flex max-w-md flex-wrap gap-1">
                                    {isEditing ? userPermissions.map(({ _id: departmentId, department }) => {
                                        const accessibility = permissions.some(
                                            (permission) => permission.department === department && permission.accessibility
                                        );
                                        return (
                                            <button
                                                type="button"
                                                key={departmentId}
                                                className={accessibility ? 'nx-chip-info' : 'nx-chip-bad'}
                                                onClick={() => handleUserPermissions(_id, department, departmentId)}
                                            >
                                                {department}
                                            </button>
                                        );
                                    }) : permissions.map(({ department }, index) => (
                                        <span className="nx-chip-info" key={`${department}-${index}`}>
                                            {department}
                                        </span>
                                    ))}
                                </div>
                            </td>
                            <td>
                                <TableActionGroup>
                                    {isEditing ? (
                                        <>

                                            <button
                                                type="button"
                                                className="nx-btn-ghost text-success"
                                                onClick={() => handleEditUser(_id, role, firstName, lastName)}
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                className="nx-btn-ghost text-danger"
                                                onClick={() => toggleEdit(_id)}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>

                                            <button
                                                type="button"
                                                className="nx-btn-ghost p-2 text-success disabled:cursor-not-allowed disabled:opacity-40"
                                                onClick={() => toggleEdit(_id)}
                                                disabled={!isActive}
                                                title="Edit user"
                                            >
                                                <HiPencilAlt className="h-5 w-5" />
                                                <span className="sr-only">Edit user</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`nx-btn-ghost p-2 ${isActive ? 'text-warning' : 'text-signal'}`}
                                                onClick={() => handleToggleUserStatus(isActive, _id, firstName)}
                                                title={isActive ? 'Disable user' : 'Activate user'}
                                            >
                                                {isActive ? <FaUserAltSlash className="h-5 w-5" /> : <FaUserCheck className="h-5 w-5" />}
                                                <span className="sr-only">{isActive ? 'Disable user' : 'Activate user'}</span>
                                            </button>
                                            <button
                                                type="button"
                                                className="nx-btn-ghost p-2 text-danger"
                                                onClick={() => handleDeleteUser(_id, firstName, lastName)}
                                                title="Delete user"
                                            >
                                                <HiTrash className="h-5 w-5" />
                                                <span className="sr-only">Delete user</span>
                                            </button>
                                        </>
                                    )}
                                </TableActionGroup>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </DataTableShell>
    );
}